import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { JobApplication } from '../types';
import { apiService } from '../services/api';

interface JobApplicationContextType {
  pendingApplicationsCount: number;
  refreshApplications: () => Promise<void>;
}

const JobApplicationContext = createContext<JobApplicationContextType | undefined>(undefined);

export const useJobApplications = () => {
  const context = useContext(JobApplicationContext);
  if (context === undefined) {
    throw new Error('useJobApplications must be used within a JobApplicationProvider');
  }
  return context;
};

interface JobApplicationProviderProps {
  children: ReactNode;
}

export const JobApplicationProvider: React.FC<JobApplicationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [applications, setApplications] = useState<JobApplication[]>([]);

  const refreshApplications = useCallback(async () => {
    if (user?.role === 'EMPLOYER' && user?.id) {
      try {
        const response = await apiService.jobApplications.getByEmployer(user.id);
        if (response?.success && response?.data) {
          setApplications(response.data);
        }
      } catch (error) {
        console.error('Error loading applications:', error);
      }
    } else {
      setApplications([]);
    }
  }, [user?.role, user?.id]);

  useEffect(() => {
    refreshApplications();
  }, [refreshApplications]);

  const pendingApplicationsCount = applications.filter(app => app.status === 'PENDING').length;

  const value: JobApplicationContextType = {
    pendingApplicationsCount,
    refreshApplications,
  };

  return (
    <JobApplicationContext.Provider value={value}>
      {children}
    </JobApplicationContext.Provider>
  );
};

